import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { Form } from '@anytag/shared-atoms';
import { TextSearchField, SingleDatePickerField } from '@anytag/shared-molecules';
import { AnalyticsSelectedAccountInfo } from '../../../types';
import { AnalyticsHashtagsList, AnalyticsHashtagsListProps } from '../../shared';
import { useCreateDeleteInstagramHashtag, useExportInstagramAnalyticsProfileHashtagPosts } from './hooks';
import { AnalyticsInstagramHashtagsFilterFormValues, AnalyticsInstagramHashtagsFilterFormKeys } from './schemaTypes';

export interface TemplateProps extends Pick<AnalyticsHashtagsListProps, 'hashtags' | 'maxHashtags'> {
  onSubmit?: () => void;
  analyticsAccountId: AnalyticsSelectedAccountInfo['id'];
}

export const Template = ({ hashtags, maxHashtags, analyticsAccountId, onSubmit }: TemplateProps) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<AnalyticsInstagramHashtagsFilterFormValues>();
  const { adding, deleting, handleAddNewHashtag, handleDeleteHashtag } = useCreateDeleteInstagramHashtag({
    hashtags,
    onSubmit,
    analyticsAccountId
  });

  const [dateValue, hashtagIdValue] = watch(['date', 'hashtagId']);
  const { ExportButton, ExportModal } = useExportInstagramAnalyticsProfileHashtagPosts({
    analyticsAccountId,
    hashtagId: hashtagIdValue
  });

  return (
    <>
      <AnalyticsHashtagsList
        adding={adding}
        deleting={deleting}
        hashtags={hashtags}
        maxHashtags={maxHashtags}
        activeHashtag={hashtagIdValue}
        onAddNewHashtag={handleAddNewHashtag}
        onDeleteHashtag={handleDeleteHashtag}
        onSelectHashtag={(hashtag) => {
          setValue('hashtagId', hashtag ? hashtag.id : '');
          onSubmit?.();
        }}
      />

      <Form.Fields className="mt-3">
        <TextSearchField<AnalyticsInstagramHashtagsFilterFormKeys>
          name="keyword"
          onSubmit={onSubmit}
          placeholder={t<string>('hashtagsAnalyticsKeywordPlaceholder')}
        />

        <SingleDatePickerField<AnalyticsInstagramHashtagsFilterFormKeys>
          name="date"
          onSubmit={onSubmit}
          disabledDays={{ after: new Date() }}
          className="w-46 flex-none"
          required
          clearable={false}
        />

        <Form.Field className="flex-none">
          <ExportButton disabled={!dateValue || !hashtagIdValue} />
        </Form.Field>
      </Form.Fields>

      {ExportModal}
    </>
  );
};
